import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from 'config/axiosInstance';
import alcohol_limit from '../../assets/icons/dashboard/alcohol_limit.svg';
import driver_submit from '../../assets/icons/dashboard/driver_submit.svg';
import total_device from '../../assets/icons/dashboard/device.svg';
import alert_icon from '../../assets/icons/dashboard/emergency.svg';
import dot from '../../assets/icons/dashboard/dot.svg';
import { useTranslation } from 'react-i18next';

const SubmissionsStatus: React.FC = () => {
  const [exceedAlcoholLimits, setExceedAlcoholLimits] = useState(0);
  const [totalDriverSubmitted, setTotalDriverSubmitted] = useState(0);
  const [totalDriverNotSubmitted, setTotalDriverNotSubmitted] = useState(0);
  const [totalDevice, setTotalDevice] = useState(0);
  const [deviceUse, setDeviceUse] = useState(0);
  const now = new Date();
  const startday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const endday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);

  const { t } = useTranslation('dashboard');

  const fetchDriverSubmitted = useCallback(async () => {
    const apiUrl = `/v1/drivers/recent_submission/?alcoholtest__testing_time__gte=${startday}T00:00:00.000000Z&alcoholtest__testing_time__lte=${endday}T23:59:59.999999Z`;
    const response = await axiosInstance.get<{
      results: { alcoholtests: [] }[];
    }>(apiUrl);

    const drivers = response.data.results;
    let submittedCount = 0;
    let notSubmittedCount = 0;

    drivers.forEach((driver) => {
      if (driver.alcoholtests.length > 0) {
        submittedCount++;
      } else {
        notSubmittedCount++;
      }
    });

    return { submittedCount, notSubmittedCount };
  }, [startday, endday]);

  const fetchData = useCallback(async () => {
    try {
      const [alcoholData, driverData, deviceData] = await Promise.all([
        fetchExceedAlcoholLimitData(),
        fetchDriverSubmitted(),
        fetchDevice(),
      ]);

      setExceedAlcoholLimits(alcoholData);
      setTotalDriverSubmitted(driverData.submittedCount);
      setTotalDriverNotSubmitted(driverData.notSubmittedCount);
      setTotalDevice(deviceData.totalDevice);
      setDeviceUse(deviceData.deviceUseCount);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [fetchDriverSubmitted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchExceedAlcoholLimitData = async () => {
    const apiUrl = `/v1/alcohol_tests/?data__gte=0.15`;
    const response = await axiosInstance.get(apiUrl);
    return response.data.count;
  };

  const fetchDevice = async () => {
    const apiUrl = '/v1/devices/';
    const response = await axiosInstance.get(apiUrl);

    const totalDevice = response.data.count;
    const devicesApiUrl = `${apiUrl}?page_size=${totalDevice}`;
    const devicesResponse = await axiosInstance.get(devicesApiUrl);

    let deviceUseCount = 0;
    devicesResponse.data.results.forEach((device: { user: [] }) => {
      if (device.user) {
        deviceUseCount++;
      }
    });

    return { totalDevice, deviceUseCount };
  };

  return (
    <section className="flex flex-row space-x-4 justify-between">
      {renderStatCard(
        alcohol_limit,
        exceedAlcoholLimits,
        t('status.alcohol-limit.info-title'),
        `${t('status.alcohol-limit.limit', { count: exceedAlcoholLimits })} ${t(
          'status.alcohol-limit.limit2'
        )}`,
        alert_icon
      )}
      {renderStatCard(
        driver_submit,
        totalDriverSubmitted,
        t('status.driver-submitted.info-title'),
        `${t('status.driver-submitted.limit')} ${totalDriverNotSubmitted}  ${t(
          'status.driver-submitted.limit2'
        )}`,
        alert_icon
      )}
      {renderStatCard(
        total_device,
        totalDevice,
        t('status.total-device.info-title'),
        `${deviceUse} ${t('status.total-device.limit')}`,
        dot
      )}
    </section>
  );
};

const renderStatCard = (
  icon: string,
  count: number,
  title: string,
  subtitle: string,
  customIcon?: string
) => (
  <div className="flex flex-1 flex-col bg-white px-3 py-2 rounded-md">
    <div className="flex flex-row justify-between">
      <p className="text-primary/300 text-h5 font-semi-bold">{count}</p>
      <img src={icon ?? customIcon} alt="" />
    </div>
    <div className="flex flex-col">
      <p className="text-body-2">{title}</p>
      <div className="flex flex-row space-x-2">
        <img src={icon ? customIcon : alert_icon} alt="" />
        <p className="text-gray-500 text-caption">{subtitle}</p>
      </div>
    </div>
  </div>
);

export default SubmissionsStatus;
